import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/AuthenticationProvider";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  Trophy,
  Users,
  ArrowUp,
  ArrowDown,
  Clock,
  User,
  Shield,
} from "lucide-react";

const MultiplayerMatchStats = ({ opponent, gameType, overrideMyStats }) => {
  const { user, organizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);
  const [opponentStats, setOpponentStats] = useState(null);
  const [headToHead, setHeadToHead] = useState(null);
  const [activeTab, setActiveTab] = useState("h2h");

  useEffect(() => {
    if (overrideMyStats) {
      setMyStats((prev) => ({
        ...prev,
        ...overrideMyStats,
      }));
    }
  }, [overrideMyStats]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!organizationId || !user?.email || !opponent?.email) {
        setLoading(false);
        return;
      }

      try {
        // Fetch current user's history from gameHistory subcollection
        const myHistoryRef = collection(
          db,
          `organizations/${organizationId}/users/${user.email}/gameHistory`
        );

        // Query for multiplayer games
        const myGamesQuery = query(
          myHistoryRef,
          where("mode", "==", "multiplayer")
        );
        const myGamesSnapshot = await getDocs(myGamesQuery);

        // Aggregate stats from history
        const myAggregatedStats = aggregateStats(myGamesSnapshot, user.email);
        setMyStats(myAggregatedStats);

        // Extract head-to-head data from my games against this opponent
        const myH2HGames = myGamesSnapshot.docs.filter(
          (doc) => doc.data().opponent?.email === opponent.email
        );

        if (myH2HGames.length > 0) {
          const h2hStats = calculateH2HStats(myH2HGames, user.email);
          setHeadToHead({
            fromMyPerspective: h2hStats,
          });
        }

        // Fetch opponent's game history
        const opponentHistoryRef = collection(
          db,
          `organizations/${organizationId}/users/${opponent.email}/gameHistory`
        );

        // Query for multiplayer games
        const opponentGamesQuery = query(
          opponentHistoryRef,
          where("mode", "==", "multiplayer")
        );
        const opponentGamesSnapshot = await getDocs(opponentGamesQuery);

        // Aggregate opponent stats
        const opponentAggregatedStats = aggregateStats(
          opponentGamesSnapshot,
          opponent.email
        );
        setOpponentStats(opponentAggregatedStats);

        // Complement head-to-head data from opponent's games against the current user
        if (headToHead) {
          const opponentH2HGames = opponentGamesSnapshot.docs.filter(
            (doc) => doc.data().opponent?.email === user.email
          );

          if (opponentH2HGames.length > 0) {
            const opponentH2HStats = calculateH2HStats(
              opponentH2HGames,
              opponent.email
            );
            setHeadToHead((prev) => ({
              ...prev,
              fromOpponentPerspective: opponentH2HStats,
            }));
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching player stats:", error);
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.email, opponent?.email, organizationId, gameType]);

  // Helper function to aggregate stats from game history documents
  const aggregateStats = (gamesSnapshot, playerEmail) => {
    const stats = {
      totalGamesPlayed: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      totalScore: 0,
      byGame: {},
      opponents: {},
    };

    gamesSnapshot.forEach((doc) => {
      const game = doc.data();
      const gameTypeKey = game.gameType;

      // Update total stats
      stats.totalGamesPlayed++;
      stats.totalScore += game.score || 0;

      // Update result stats
      if (game.result === "win") stats.wins++;
      else if (game.result === "loss") stats.losses++;
      else if (game.result === "draw") stats.draws++;

      // Update game-specific stats
      if (!stats.byGame[gameTypeKey]) {
        stats.byGame[gameTypeKey] = {
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          totalScore: 0,
          highScore: 0,
        };
      }

      stats.byGame[gameTypeKey].gamesPlayed++;
      stats.byGame[gameTypeKey].totalScore += game.score || 0;
      stats.byGame[gameTypeKey].highScore = Math.max(
        stats.byGame[gameTypeKey].highScore,
        game.score || 0
      );

      if (game.result === "win") stats.byGame[gameTypeKey].wins++;
      else if (game.result === "loss") stats.byGame[gameTypeKey].losses++;
      else if (game.result === "draw") stats.byGame[gameTypeKey].draws++;

      // Update opponent stats
      if (game.opponent && game.opponent.email) {
        const opponentEmail = game.opponent.email;

        if (!stats.opponents[opponentEmail]) {
          stats.opponents[opponentEmail] = {
            gamesPlayed: 0,
            wins: 0,
            losses: 0,
            draws: 0,
            opponentName: game.opponent.name || opponentEmail.split("@")[0],
            lastPlayed: game.timestamp,
          };
        }

        stats.opponents[opponentEmail].gamesPlayed++;

        if (game.result === "win") stats.opponents[opponentEmail].wins++;
        else if (game.result === "loss")
          stats.opponents[opponentEmail].losses++;
        else if (game.result === "draw") stats.opponents[opponentEmail].draws++;

        // Update last played if this game is more recent
        if (
          game.timestamp &&
          (!stats.opponents[opponentEmail].lastPlayed ||
            game.timestamp.toMillis() >
              stats.opponents[opponentEmail].lastPlayed.toMillis())
        ) {
          stats.opponents[opponentEmail].lastPlayed = game.timestamp;
        }
      }
    });

    return stats;
  };

  // Helper function to calculate head-to-head stats
  const calculateH2HStats = (h2hGames, playerEmail) => {
    const stats = {
      wins: 0,
      losses: 0,
      draws: 0,
      gamesPlayed: h2hGames.length,
    };

    h2hGames.forEach((doc) => {
      const game = doc.data();
      if (game.result === "win") stats.wins++;
      else if (game.result === "loss") stats.losses++;
      else if (game.result === "draw") stats.draws++;
    });

    return stats;
  };

  // Get game-specific stats
  const getGameStats = (statsObj) => {
    if (!statsObj || !statsObj.byGame || !statsObj.byGame[gameType]) {
      return null;
    }
    return statsObj.byGame[gameType];
  };

  const myGameStats = getGameStats(myStats);
  const opponentGameStats = getGameStats(opponentStats);

  if (loading) {
    return (
      <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex justify-center items-center h-24">
          <div className="animate-spin h-6 w-6 border-4 border-emerald-500 rounded-full border-t-transparent"></div>
        </div>
      </div>
    );
  }

  // If we don't have any stats yet
  if (!myStats && !opponentStats) {
    return null;
  }

  // Calculate win rates
  const calculateWinRate = (stats) => {
    if (!stats || !stats.totalGamesPlayed || stats.totalGamesPlayed === 0)
      return 0;
    return Math.round((stats.wins / stats.totalGamesPlayed) * 100);
  };

  const myWinRate = calculateWinRate(myStats);
  const opponentWinRate = calculateWinRate(opponentStats);
  const myGameWinRate = myGameStats ? calculateWinRate(myGameStats) : 0;
  const opponentGameWinRate = opponentGameStats
    ? calculateWinRate(opponentGameStats)
    : 0;

  return (
    <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
      <h3 className="text-lg font-bold text-white text-center mb-3">
        Match History
      </h3>

      {/* Tabs */}
      <div className="flex justify-center mb-4">
        <div className="bg-slate-900 rounded-lg p-1 flex">
          <button
            onClick={() => setActiveTab("h2h")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === "h2h"
                ? "bg-slate-700 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Head to Head
          </button>
          <button
            onClick={() => setActiveTab("game")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === "game"
                ? "bg-slate-700 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            {gameType
              .replace(/-/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </button>
          <button
            onClick={() => setActiveTab("overall")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeTab === "overall"
                ? "bg-slate-700 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Overall Stats
          </button>
        </div>
      </div>

      {/* Head to Head Tab */}
      {activeTab === "h2h" && (
        <div>
          {headToHead ? (
            <div className="space-y-4">
              <div className="bg-slate-900/60 p-4 rounded-lg border border-slate-700">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Trophy size={18} className="text-emerald-400" />
                  Your Record vs {opponent.name}
                </h4>

                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <ArrowUp size={14} className="text-emerald-400" />
                      <span className="text-emerald-400 text-sm">
                        {headToHead.fromMyPerspective?.wins || 0}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <ArrowDown size={14} className="text-red-400" />
                      <span className="text-red-400 text-sm">
                        {headToHead.fromMyPerspective?.losses || 0}
                      </span>
                    </div>

                    <div className="text-amber-400 text-sm">
                      Draws: {headToHead.fromMyPerspective?.draws || 0}
                    </div>
                  </div>

                  <div className="text-white text-xs px-2 py-1 bg-slate-800 rounded">
                    {headToHead.fromMyPerspective?.gamesPlayed || 0} games
                  </div>
                </div>

                <div className="mt-3 w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full"
                    style={{
                      width: `${
                        headToHead.fromMyPerspective?.gamesPlayed > 0
                          ? (headToHead.fromMyPerspective.wins /
                              headToHead.fromMyPerspective.gamesPlayed) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Game Type Breakdown */}
              {myStats?.byGame && Object.keys(myStats.byGame).length > 0 && (
                <div className="mt-2">
                  <h4 className="text-white text-sm font-medium mb-2">
                    Game Breakdown:
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                    {Object.entries(myStats.byGame)
                      .filter(([game, gameStats]) => gameStats.gamesPlayed > 0)
                      .sort((a, b) => b[1].gamesPlayed - a[1].gamesPlayed)
                      .map(([game, gameStats]) => (
                        <div
                          key={game}
                          className={`p-2 rounded-lg text-xs flex justify-between items-center ${
                            game === gameType
                              ? "bg-emerald-900/20 border border-emerald-800/30"
                              : "bg-slate-900/40"
                          }`}
                        >
                          <div>
                            <span className="text-white">
                              {game
                                .replace(/-/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-400">
                              {gameStats.wins}W
                            </span>
                            <span className="text-red-400">
                              {gameStats.losses}L
                            </span>
                            <span className="text-amber-400">
                              {gameStats.draws}D
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <Users className="text-slate-600 mb-2" size={24} />
              <p className="text-slate-400">
                No previous matches with this player
              </p>
              <p className="text-slate-500 text-xs mt-1">
                This is your first match together!
              </p>
            </div>
          )}
        </div>
      )}

      {/* Game-specific Stats Tab */}
      {activeTab === "game" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
            <h4 className="text-white text-sm font-medium mb-2">Your Stats</h4>

            {myGameStats ? (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Games</span>
                  <span className="text-white">
                    {myGameStats.gamesPlayed || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Win Rate</span>
                  <span className="text-emerald-400">{myGameWinRate}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Wins</span>
                  <span className="text-emerald-400">
                    {myGameStats.wins || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Losses</span>
                  <span className="text-red-400">
                    {myGameStats.losses || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Draws</span>
                  <span className="text-amber-400">
                    {myGameStats.draws || 0}
                  </span>
                </div>
                {myGameStats.highScore && (
                  <div className="flex justify-between text-xs border-t border-slate-800 pt-1 mt-1">
                    <span className="text-slate-400">High Score</span>
                    <span className="text-purple-400">
                      {myGameStats.highScore}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-xs mt-1">No previous games</p>
            )}
          </div>

          <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
            <h4 className="text-white text-sm font-medium mb-2">
              {opponent.name}'s Stats
            </h4>

            {opponentGameStats ? (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Games</span>
                  <span className="text-white">
                    {opponentGameStats.gamesPlayed || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Win Rate</span>
                  <span className="text-emerald-400">
                    {opponentGameWinRate}%
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Wins</span>
                  <span className="text-emerald-400">
                    {opponentGameStats.wins || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Losses</span>
                  <span className="text-red-400">
                    {opponentGameStats.losses || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Draws</span>
                  <span className="text-amber-400">
                    {opponentGameStats.draws || 0}
                  </span>
                </div>
                {opponentGameStats.highScore && (
                  <div className="flex justify-between text-xs border-t border-slate-800 pt-1 mt-1">
                    <span className="text-slate-400">High Score</span>
                    <span className="text-purple-400">
                      {opponentGameStats.highScore}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-xs mt-1">No previous games</p>
            )}
          </div>

          {(myGameStats || opponentGameStats) && (
            <div className="col-span-2 mt-1 p-2 bg-slate-800/40 rounded-lg text-xs text-slate-400">
              <div className="flex items-center gap-1">
                <Clock size={12} />
                <span>
                  Stats for{" "}
                  {gameType
                    .replace(/-/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overall Stats Tab */}
      {activeTab === "overall" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
            <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-1">
              <User size={14} className="text-emerald-400" />
              Your Overall Stats
            </h4>

            {myStats ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-slate-400">Win Rate</div>
                  <div className="bg-slate-800 px-2 py-0.5 rounded text-xs font-medium text-emerald-400">
                    {myWinRate}%
                  </div>
                </div>

                <div className="flex justify-center gap-3 mb-2">
                  <div className="flex flex-col items-center">
                    <div className="text-emerald-400 font-medium">
                      {myStats.wins || 0}
                    </div>
                    <div className="text-xs text-slate-400">Wins</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-red-400 font-medium">
                      {myStats.losses || 0}
                    </div>
                    <div className="text-xs text-slate-400">Losses</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-amber-400 font-medium">
                      {myStats.draws || 0}
                    </div>
                    <div className="text-xs text-slate-400">Draws</div>
                  </div>
                </div>

                <div className="flex justify-between text-xs border-t border-slate-800 pt-1">
                  <span className="text-slate-400">Total Games</span>
                  <span className="text-white">
                    {myStats.totalGamesPlayed || 0}
                  </span>
                </div>

                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Total Score</span>
                  <span className="text-purple-400">
                    {myStats.totalScore || 0}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-xs mt-1">No stats available</p>
            )}
          </div>

          <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700">
            <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-1">
              <User size={14} className="text-blue-400" />
              {opponent.name}'s Stats
            </h4>

            {opponentStats ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-slate-400">Win Rate</div>
                  <div className="bg-slate-800 px-2 py-0.5 rounded text-xs font-medium text-emerald-400">
                    {opponentWinRate}%
                  </div>
                </div>

                <div className="flex justify-center gap-3 mb-2">
                  <div className="flex flex-col items-center">
                    <div className="text-emerald-400 font-medium">
                      {opponentStats.wins || 0}
                    </div>
                    <div className="text-xs text-slate-400">Wins</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-red-400 font-medium">
                      {opponentStats.losses || 0}
                    </div>
                    <div className="text-xs text-slate-400">Losses</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-amber-400 font-medium">
                      {opponentStats.draws || 0}
                    </div>
                    <div className="text-xs text-slate-400">Draws</div>
                  </div>
                </div>

                <div className="flex justify-between text-xs border-t border-slate-800 pt-1">
                  <span className="text-slate-400">Total Games</span>
                  <span className="text-white">
                    {opponentStats.totalGamesPlayed || 0}
                  </span>
                </div>

                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Total Score</span>
                  <span className="text-purple-400">
                    {opponentStats.totalScore || 0}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-xs mt-1">No stats available</p>
            )}
          </div>

          <div className="col-span-2 mt-1 p-2 bg-slate-800/40 rounded-lg text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <Shield size={12} />
              <span>Multiplayer career stats across all games</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerMatchStats;
